import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import type { HomePack } from "./types";
import { PackVisual } from "../fx/PackVisual";
import { HoloCard } from "../fx/HoloCard";
import { Particles } from "../fx/Particles";
import { TierBadge } from "../storefront/TierBadge";
import { rm } from "../../lib/format";
import { EASE } from "../../lib/motion";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";
import { ListingTitle } from "../ListingTitle";

/**
 * The hero pack unit — a floating, tilt-reactive foil pack that sells the core
 * ritual. Used large on desktop and as a full-bleed band on mobile.
 */
export function PackSpotlight({ pack, size = "lg" }: { pack: HomePack; size?: "lg" | "md" }) {
  const reduced = usePrefersReducedMotion();
  const big = size === "lg";

  return (
    <Link
      to={`/packs/${pack.id}`}
      className="group relative block overflow-hidden rounded-3xl neon-ring bg-mesh-deep bg-noise"
    >
      <Particles count={12} />
      <div
        className={`relative flex items-center gap-5 px-5 ${big ? "py-8 lg:gap-8 lg:px-8 lg:py-10" : "py-6"}`}
      >
        {/* float-soft is the OUTER wrapper so HoloCard's sheen (sized to its
            own inner div) bobs rigidly together with the pack, instead of the
            sheen sitting still while only the pack inside it moves. */}
        <motion.div
          className={reduced ? "" : "float-soft"}
          initial={{ opacity: 0, y: 24, rotate: -6 }}
          animate={{ opacity: 1, y: 0, rotate: -4 }}
          transition={{ duration: 0.7, ease: EASE.outExpo }}
        >
          <HoloCard holo={0.32} max={12} lift={16}>
            <PackVisual
              id={pack.id}
              name={pack.name}
              images={pack.images}
              foilLabel={pack.foilLabel}
              foilColor={pack.foilColor}
              className={big ? "h-[190px] w-[133px] lg:h-[250px] lg:w-[175px]" : "h-[150px] w-[105px]"}
            />
          </HoloCard>
        </motion.div>

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.28em] text-brand-pink">
            <Sparkles size={11} /> Featured pack
          </p>
          <ListingTitle
            as="h3"
            className={`mt-2 font-display font-extrabold text-white ${big ? "text-lg lg:text-2xl min-h-7 lg:min-h-8 leading-tight" : "text-base min-h-5 leading-tight"}`}
          >
            {pack.name}
          </ListingTitle>
          <p className="mt-2 text-[11px] leading-relaxed text-white/50">
            Pick your pack from the tray, tear the foil, then flip each card one at a time.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {pack.topTier && (
              <TierBadge name={pack.topTier} color={pack.topTierColor ?? undefined} glow size="sm" />
            )}
            <span className="rounded-full bg-white/[0.07] px-2.5 py-1 text-[10px] text-white/60">
              {pack.totalRemaining} cards left
            </span>
          </div>

          <span className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-brand px-4 py-2.5 text-xs font-bold shadow-lg shadow-brand-pink/25 transition-transform group-hover:scale-105">
            Rip it open · {rm(pack.price)}
          </span>
        </div>
      </div>
    </Link>
  );
}
